// src/components/common/Toast.js
import React, { useEffect, useState, useCallback, createContext, useContext } from "react";
import { IconCheck, IconX, IconInfo } from "./Icons";

const ToastContext = createContext(null);

const ICONS = {
  success: <IconCheck size={16} stroke="#22c55e" />,
  error: <IconX size={16} stroke="#ef4444" />,
  info: <IconInfo size={16} stroke="#3b82f6" />,
};

const BORDER = {
  success: "border-green-500/30",
  error: "border-red-500/30",
  info: "border-blue-500/30",
};

function Toast({ id, message, type = "info", onRemove }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onRemove(id), 300);
    }, 4000);
    return () => clearTimeout(timer);
  }, [id, onRemove]);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border bg-onyx-900/95 backdrop-blur-sm
        ${BORDER[type]} ${exiting ? "opacity-0 translate-x-8" : "opacity-100 translate-x-0"}
        transition-all duration-300`}
    >
      {ICONS[type]}
      <span className="text-sm font-body text-onyx-100">{message}</span>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <Toast key={t.id} {...t} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be inside ToastProvider");
  return ctx;
}
